import { toast } from '@britishcouncil/react-admin';
import { DOWNLOAD_FILETYPES, useFileDownload } from '@britishcouncil/react-common';
import { lrwSessionExamMaterialsClient } from 'api/reports';
import { Centered, NonSortableTableHeaderCell } from 'components';
import { useLrwSessionAllowedActions } from 'hooks';
import { useState } from 'react';
import {
  Button,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TabPane,
} from 'semantic-ui-react';
import { parseBlobError, transformResultToDataWithFilename } from 'utils';

type ExamMaterialType = 'lrw-credentials' | 'desk-labels' | 'attendance-register';

interface ExamMaterial {
  name: string;
  type: ExamMaterialType;
}

const EXAM_MATERIALS: ExamMaterial[] = [
  {
    name: 'LRW Credentials',
    type: 'lrw-credentials',
  },
  {
    name: 'Desk Labels',
    type: 'desk-labels',
  },
  {
    name: 'Attendance Register',
    type: 'attendance-register',
  },
];

interface ExamMaterialsProps {
  lrwSessionId: number;
}

export const ExamMaterials = ({ lrwSessionId }: ExamMaterialsProps) => {
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending' | undefined>(
    'ascending'
  );
  const [printingMaterials, setPrintingMaterials] = useState<Set<ExamMaterialType>>(new Set());
  const { download } = useFileDownload();
  const { canPrintExamMaterials } = useLrwSessionAllowedActions();

  const handlePrint = async (type: ExamMaterialType) => {
    if (printingMaterials.has(type)) {
      return;
    }

    setPrintingMaterials((current) => {
      const next = new Set(current);
      next.add(type);
      return next;
    });

    try {
      if (type === 'lrw-credentials') {
        const response = await lrwSessionExamMaterialsClient.getLrwCredentials(lrwSessionId, {
          responseType: 'blob',
          customErrorHandling: true,
        });

        const result = transformResultToDataWithFilename(response.data, response.headers);
        download(result.data, result.filename ?? 'lrw-credentials.pdf', DOWNLOAD_FILETYPES.pdf);
      }

      if (type === 'desk-labels') {
        const response = await lrwSessionExamMaterialsClient.getDeskLabels(lrwSessionId, {
          responseType: 'blob',
          customErrorHandling: true,
        });

        const result = transformResultToDataWithFilename(response.data, response.headers);
        download(result.data, result.filename ?? 'desk-labels.pdf', DOWNLOAD_FILETYPES.pdf);
      }

      if (type === 'attendance-register') {
        const response = await lrwSessionExamMaterialsClient.getAttendanceRegister(lrwSessionId, {
          responseType: 'blob',
          customErrorHandling: true,
        });

        const result = transformResultToDataWithFilename(response.data, response.headers);
        download(result.data, result.filename ?? 'attendance-register.pdf', DOWNLOAD_FILETYPES.pdf);
      }
    } catch (error) {
      const serverErrors = await parseBlobError(error);
      serverErrors.forEach(({ message }) => {
        toast({
          type: 'warning',
          title: 'Warning',
          message,
        });
      });
    } finally {
      setPrintingMaterials((current) => {
        const next = new Set(current);
        next.delete(type);
        return next;
      });
    }
  };

  const handleSort = () => {
    setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
  };

  const sortedExamMaterials = [...EXAM_MATERIALS].sort((a, b) => {
    if (sortDirection === 'ascending') {
      return a.name.localeCompare(b.name);
    } else {
      return b.name.localeCompare(a.name);
    }
  });

  return (
    <TabPane>
      <Table sortable celled fixed>
        <TableHeader>
          <TableRow>
            <TableHeaderCell sorted={sortDirection} onClick={handleSort}>
              Name
            </TableHeaderCell>
            <NonSortableTableHeaderCell width="1" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedExamMaterials.map(({ name, type }) => (
            <TableRow key={name}>
              <TableCell>{name}</TableCell>
              <TableCell>
                <Centered>
                  <Button
                    icon
                    onClick={() => handlePrint(type)}
                    disabled={!canPrintExamMaterials || printingMaterials.has(type)}
                  >
                    <Icon
                      name={printingMaterials.has(type) ? 'spinner' : 'print'}
                      loading={printingMaterials.has(type)}
                    />
                  </Button>
                </Centered>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TabPane>
  );
};
